import React, {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from "react";
import { toast } from "react-toastify";
import Utils from "../configs/Utils";
import Enums from "../configs/Enums";

/**
 * Tracks PayPal background-job state across the Gateways page.
 *
 * - `activeJobs` — currently queued/running jobs for the user.  Polled every
 *   1.5s while at least one is active, otherwise every 8s as a heartbeat
 *   so newly enqueued jobs are picked up quickly.
 * - `lastJobByType` — last seen state per job type so the inline progress
 *   bars in PaypalTools / PaypalDisputes can keep displaying the result
 *   message after the job finishes (and falls off the active list).
 * - `submitJob` / `cancelJob` / `deleteJob` — thin REST wrappers that
 *   refresh state immediately on success.
 */
const PaypalJobsContext = createContext(null);

const ACTIVE_POLL_MS = 1500;
const IDLE_POLL_MS = 8000;

export const PaypalJobsProvider = ({ children }) => {
  const [activeJobs, setActiveJobs] = useState([]);
  const [lastJobByType, setLastJobByType] = useState({});
  const [historyJobs, setHistoryJobs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const pollTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const visibleRef = useRef(typeof document === "undefined" ? true : !document.hidden);
  // Mirror of lastJobByType so refreshActive can read latest state without
  // depending on it (which would re-create the callback and break the poll
  // loop's empty-deps useEffect, leaving it stuck on a stale closure).
  const lastJobByTypeRef = useRef({});
  useEffect(() => { lastJobByTypeRef.current = lastJobByType; }, [lastJobByType]);

  const refreshActive = useCallback(async () => {
    try {
      const r = await Utils._apiRequest({
        path: Enums.endpoints.paypal.JOBS_ACTIVE,
        type: "GET",
      });
      if (!mountedRef.current) return null;
      if (r?.ok) {
        const items = r.items || [];
        setActiveJobs(items);
        setLastSyncAt(new Date());
        // Merge active jobs into per-type map.
        setLastJobByType((prev) => {
          const next = { ...prev };
          items.forEach((j) => { next[j.type] = j; });
          return next;
        });
        // Detect stale entries: types we previously saw as queued/running
        // that are no longer in the active list — fetch their final state.
        const nowActiveIds = new Set(items.map((j) => String(j._id)));
        const stalePrev = Object.values(lastJobByTypeRef.current).filter(
          (j) => j && (j.status === "queued" || j.status === "running") && !nowActiveIds.has(String(j._id)),
        );
        if (stalePrev.length) {
          const fetched = await Promise.all(stalePrev.map(async (s) => {
            try {
              const d = await Utils._apiRequest({
                path: Enums.endpoints.paypal.JOBS_DETAIL,
                pathParams: { id: s._id },
                type: "GET",
              });
              return d?.item || null;
            } catch { return null; }
          }));
          if (!mountedRef.current) return null;
          setLastJobByType((prev) => {
            const next = { ...prev };
            fetched.forEach((j) => { if (j) next[j.type] = j; });
            return next;
          });
        }
        return items;
      }
    } catch { /* ignore poll errors */ }
    return null;
  }, []);

  // Seed lastJobByType on mount so inline progress cards (in PaypalTools /
  // PaypalDisputes) immediately show the last known result of each type
  // after a page reload — even if the job already finished.
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const r = await Utils._apiRequest({
          path: Enums.endpoints.paypal.JOBS_LIST,
          type: "GET",
          query: { limit: 30 },
        });
        if (aborted || !mountedRef.current || !r?.ok) return;
        const seed = {};
        // items are sorted desc by createdAt, so the first occurrence per
        // type wins.
        (r.items || []).forEach((j) => {
          if (!seed[j.type]) seed[j.type] = j;
        });
        setLastJobByType((prev) => ({ ...seed, ...prev }));
      } catch { /* ignore */ }
    })();
    return () => { aborted = true; };
  }, []);

  // Poll loop — adjust cadence based on whether anything is active.
  // Pauses while the tab is hidden (Page Visibility API) to save bandwidth
  // and resumes immediately on visibilitychange so users see fresh state.
  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;

    const schedule = (delay) => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      pollTimerRef.current = setTimeout(tick, delay);
    };

    const tick = async () => {
      if (cancelled) return;
      if (!visibleRef.current) {
        // Recheck visibility every few seconds without hitting the API.
        schedule(IDLE_POLL_MS);
        return;
      }
      const items = await refreshActive();
      if (cancelled) return;
      const delay = (items && items.length) ? ACTIVE_POLL_MS : IDLE_POLL_MS;
      schedule(delay);
    };

    const onVisibility = () => {
      const visible = !document.hidden;
      visibleRef.current = visible;
      if (visible) {
        // Refresh immediately when the user returns.
        schedule(0);
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }

    tick();
    return () => {
      cancelled = true;
      mountedRef.current = false;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitJob = useCallback(async (type, payload, label) => {
    const r = await Utils._apiRequest({
      path: Enums.endpoints.paypal.JOBS_CREATE,
      type: "POST",
      payload: { type, payload, label },
    });
    if (!r?.ok) {
      const msg = r?.errors || "Submit job thất bại";
      toast.error(msg);
      throw new Error(msg);
    }
    setLastJobByType((prev) => ({ ...prev, [type]: r.item }));
    refreshActive();
    return r.item;
  }, [refreshActive]);

  const cancelJob = useCallback(async (id) => {
    const r = await Utils._apiRequest({
      path: Enums.endpoints.paypal.JOBS_CANCEL,
      pathParams: { id },
      type: "POST",
    });
    if (!r?.ok) throw new Error(r?.errors || "Cancel thất bại");
    refreshActive();
    return r.item;
  }, [refreshActive]);

  const deleteJob = useCallback(async (id) => {
    const r = await Utils._apiRequest({
      path: Enums.endpoints.paypal.JOBS_DELETE,
      pathParams: { id },
      type: "DELETE",
    });
    if (!r?.ok) throw new Error(r?.errors || "Xóa thất bại");
    return true;
  }, []);

  const clearFinished = useCallback(async (filter = {}) => {
    const r = await Utils._apiRequest({
      path: Enums.endpoints.paypal.JOBS_CLEAR_FINISHED,
      type: "DELETE",
      query: filter,
    });
    if (!r?.ok) throw new Error(r?.errors || "Xoá hàng loạt thất bại");
    return r.deleted || 0;
  }, []);

  const fetchHistory = useCallback(async (filter = {}) => {
    setHistoryLoading(true);
    try {
      const r = await Utils._apiRequest({
        path: Enums.endpoints.paypal.JOBS_LIST,
        type: "GET",
        query: { limit: 100, ...filter },
      });
      if (r?.ok) setHistoryJobs(r.items || []);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const value = {
    activeJobs,
    lastJobByType,
    historyJobs,
    historyLoading,
    lastSyncAt,
    submitJob,
    cancelJob,
    deleteJob,
    clearFinished,
    refreshActive,
    fetchHistory,
  };

  return <PaypalJobsContext.Provider value={value}>{children}</PaypalJobsContext.Provider>;
};

export const usePaypalJobs = () => {
  const ctx = useContext(PaypalJobsContext);
  if (!ctx) {
    throw new Error("usePaypalJobs must be used inside <PaypalJobsProvider>");
  }
  return ctx;
};
